import React, {useMemo} from "react";
import {Text, Stack, Box, Label} from "@nike/podium";
import {BucketData, ActionedRecord, BUCKET_CONFIGS} from "../../common/data/types";
import {adjUnits} from "../../common/data/imbalanceHelpers";

type SummaryCardsProps = {
    bucketData: BucketData;
    actionedCodes: Record<string, ActionedRecord>;
};

export function SummaryCards({bucketData, actionedCodes}: SummaryCardsProps) {
    return (
        <Stack horizontal gap="s" className="im-ca-summary">
            {BUCKET_CONFIGS.map(config => (
                <SummaryCard
                    key={config.key}
                    config={config}
                    rows={bucketData[config.key]}
                    actionedCodes={actionedCodes}
                />
            ))}
        </Stack>
    );
}

function SummaryCard({config, rows, actionedCodes}) {
    const totalUnits = useMemo(() => rows.reduce((s, item) => s + adjUnits(item), 0), [rows]);

    const isDone = rows.length > 0 && rows.every(item => actionedCodes[item.material]?.status === 'processed');
    const processedUnits = isDone ? totalUnits : 0;
    const remainingCount = isDone ? 0 : rows.length;
    const remainingUnits = isDone ? 0 : totalUnits;

    return (
        <Box className="im-ca-card">
            <Stack horizontal align="center" justify="between" className="im-ca-card-head">
                <Text font="body-3" weight="bold" as="div">
                    {config.name} — {config.actionLabel}
                </Text>
                <Stack horizontal gap="xs" className="im-ca-card-badges">
                    <Text font="body-4" as="span" className={`im-badge im-${config.adjBadgeClass}`}>{config.adjBadgeText}</Text>
                    <Text font="body-4" as="span" className={`im-badge im-${config.lcBadgeClass}`}>
                        {config.lcDotColor && (
                            <span className="im-lc-dot" style={{background: config.lcDotColor}} />
                        )}
                        {config.lcBadgeText}
                    </Text>
                </Stack>
            </Stack>
            <Stack horizontal gap="m" className="im-ca-stats">
                <Stack gap="xs" className="im-ca-stat">
                    <Label font="body-4" className="im-ca-stat-lbl">Records</Label>
                    <Text font="title-3" as="span" className={`im-ca-stat-val ${config.key === 'zero' ? 'warn' : ''}`}>
                        {remainingCount}
                    </Text>
                    <Text font="body-4" color="secondary" as="span">
                        {isDone ? 'remaining' : 'to adjust'}
                    </Text>
                </Stack>
                <Stack gap="xs" className="im-ca-stat">
                    <Label font="body-4" className="im-ca-stat-lbl">Total units</Label>
                    <Text font="title-3" as="span" className="im-ca-stat-val">{remainingUnits}</Text>
                    <Text font="body-4" color="secondary" as="span">
                        {isDone ? 'total units remaining' : config.unitLabel}
                    </Text>
                </Stack>
                {isDone && (
                    <Stack gap="xs" className="im-ca-stat im-ca-stat-done">
                        <Label font="body-4" className="im-ca-stat-lbl">Total units cleaned</Label>
                        <Text font="title-3" as="span" className="im-ca-stat-val">{processedUnits}</Text>
                        <Text font="body-4" as="span" color="success">successfully processed</Text>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}
